import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import flatten from 'flat-util';

import { ALERT_CODES } from 'src/components/JobChecklistForm/constants';
import { CollapsibleBlock } from 'src/components/SurveyForm/constants';
import { DatePickerField } from 'src/components/DatePickerField/DatePickerField';
import { FormRow } from 'src/components/Form';
import { LawMenuList } from 'src/components/JobChecklistForm/LawMenuList';
import { LawOption } from 'src/components/JobChecklistForm/LawOption';
import { LawSingleValue } from 'src/components/JobChecklistForm/LawSingleValue';
import { QuestionRow } from 'src/components/JobDatasheet/QuestionRow';
import { ReportReviewBlock } from 'src/components/ReportReviewBlock';
import { ResultFamilySectionPropTypes } from 'src/components/JobDatasheet/propTypes';
import { ResultHistoriesBlock } from 'src/components/ResultHistoriesBlock';
import { ResultSectionToggleButtons } from 'src/components/JobDatasheet/ResultSectionToggleButtons';
import { withJobContext } from 'src/utils/context/JobContext';
import Attachments from 'src/components/Attachments';
import ChecklistStyles from 'src/components/JobChecklistForm/JobChecklistForm.styles';
import DatasheetStyles from 'src/components/JobDatasheet/JobDatasheetForm/JobDatasheetForm.styles';
import SelectBox from 'src/components/SelectBox';
import TextField from 'src/components/TextField';

// TODO: PHX-1526 share between checklist and datasheet
const ResultFamilySectionComponent = ({
  jobContext,
  activeQuestion,
  activeReportReviewComments,
  activeReportReviewNote,
  activeSection,
  addNote,
  currentResult,
  datasheetCurrentVersionId,
  datasheetId,
  datasheetResultFamily,
  dispatchSaveStatus,
  factory,
  getResult,
  getResultAttachments,
  getResultExternalAttachments,
  getValidationErrors,
  hasSaqService,
  hasUnverifiedSaqService,
  isBlockSaveFailed,
  isBlockSaveSuccess,
  isCreateNote,
  isSidebarExpanded,
  isUnderReviewing,
  jobId,
  onDeletingAttachment,
  onDeletingExternalAttachment,
  readonly,
  refetchReportReview,
  resultHistories,
  selectedQuestion,
  setIsCreateNote,
  setResult,
  setShowHistory,
  setShowNote,
  setShowResult,
  showHistory,
  showNote,
  showResult,
  statistic,
}) => {
  const [displayFollowUp, setDisplayFollowUp] = useState(false);
  const [isFiltered, setIsFiltered] = useState(true);
  const [collapsedBlocks, setCollapsedBlocks] = useState(Object.values(CollapsibleBlock));
  const [comments, setComments] = useState(getResult(activeQuestion).comments);
  const [isExtAttchmentCollapsed, setIsExtAttchmentCollapsed] = useState(true);

  const { t } = useTranslation();

  const {
    job,
    user: {
      isAuditor,
      isReportReviewer,
      isInternalUser,
    },
    permissions: {
      isReportNotesAvailableToAuditor,
    },
  } = jobContext;

  useEffect(() => {
    setComments(getResult(activeQuestion).comments);
    setCollapsedBlocks(Object.values(CollapsibleBlock));
    setIsExtAttchmentCollapsed(getResultExternalAttachments(activeQuestion).length === 0);
  }, [activeQuestion]);

  const toggleBlock = id => {
    const newCollapsedBlocks = [...collapsedBlocks];
    if (collapsedBlocks.indexOf(id) === -1) {
      newCollapsedBlocks.push(id);
    } else {
      newCollapsedBlocks.splice(newCollapsedBlocks.indexOf(id), 1);
    }
    setCollapsedBlocks(newCollapsedBlocks);
  };

  const activeQuestionCurrentVersion = activeQuestion?.datasheetQuestion?.currentVersion;

  const nonCompliances = activeQuestionCurrentVersion?.nonCompliances
    ? activeQuestionCurrentVersion.nonCompliances
    : [];
  const nonCompliancesOptions = nonCompliances.map(v => ({
    value: v.id,
    label: v.description,
  }));

  const addResultNonCompliance = question => {
    const newNonCompliances = [...(getResult(question).nonCompliances || [])];
    newNonCompliances.push({
      id: null,
      lawId: null,
    });
    setResult(question, 'nonCompliances', newNonCompliances);
  };

  const removeResultNonCompliance = (question, index) => {
    const newNonCompliances = [...(getResult(question).nonCompliances || [])];
    newNonCompliances.splice(index, 1);
    setResult(question, 'nonCompliances', newNonCompliances);
  };

  const setResultNonCompliance = (question, index, field, value) => {
    const newNonCompliances = [...(getResult(question).nonCompliances || [])];
    newNonCompliances[index][field] = value;
    setResult(question, 'nonCompliances', newNonCompliances);

    if (
      ['legalRequirements', 'detailedFinding', 'recommendations'].indexOf(
        field,
      ) !== -1
    ) {
      setTimeout(() => {
        setResult(
          question,
          field,
          newNonCompliances.map(c => c[field]).join('\n\n'),
        );
      }, 500);
    }

    dispatchSaveStatus({ type: 'ADD_SAVING_BLOCK', block: `nonCompliances${index}` });
  };

  const calcLegalRequirements = (question, nonCompliance) => {
    const nonComplianceLaws = flatten(activeQuestionCurrentVersion.nonCompliances
      .filter(v => v.id === nonCompliance.id)
      .map(v => v.laws), true);

    let countryLaws = [];
    if (factory) {
      const companyAddress = factory.company.currentVersion.companyAddresses[0];
      if (companyAddress && companyAddress.currentVersion.country) {
        countryLaws = companyAddress.currentVersion.country.laws;
      }
    }

    const allLawsOptions = [...nonComplianceLaws, ...countryLaws].map(v => ({
      articleSecNo: v.currentVersion.articleSecNo,
      label: v.currentVersion.legislationName,
      legalCitation: v.currentVersion.legalCitation,
      value: v.id,
    }));

    const item = allLawsOptions.find(
      v => v.value === (nonCompliance.lawId || nonCompliance.law),
    );

    if (!item) {
      return null;
    }

    let lawLabel = item.label;
    if (item.articleSecNo) {
      lawLabel += ` ${item.articleSecNo}`;
    }

    return `In accordance with ${lawLabel}, ${item.legalCitation}`;
  };

  const setResultNonComplianceByObject = (question, index, data) => {
    const newNonCompliances = [...(getResult(question).nonCompliances || [])];
    newNonCompliances[index] = {
      ...newNonCompliances[index],
      ...data,
    };
    setResult(question, 'nonCompliances', newNonCompliances);

    Object.keys(data).forEach(field => {
      if (
        ['legalRequirements', 'detailedFinding', 'recommendations'].indexOf(
          field,
        ) !== -1
      ) {
        setTimeout(() => {
          setResult(
            question,
            field,
            newNonCompliances.map(c => c[field]).join('\n\n'),
          );
        }, 500);
      }
      if (['law', 'lawId'].indexOf(field) !== -1) {
        setTimeout(() => {
          setResultNonCompliance(
            question,
            index,
            'legalRequirements',
            calcLegalRequirements(question, newNonCompliances[index]),
          );
        }, 500);
      }
    });

    dispatchSaveStatus({ type: 'ADD_SAVING_BLOCK', block: `nonCompliances${index}` });
  };

  const outcomes =
    ((datasheetResultFamily || {}).currentVersion || {})
      .resultFamilyOutcomes || [];
  const positiveOutcome =
    outcomes.find(
      v => v.resultCategory.currentVersion.name === 'Positive Outcome',
    ) || {};
  const negativeOutcome =
    outcomes.find(
      v => v.resultCategory.currentVersion.name === 'Negative Outcome',
    ) || {};

  const isCollapsedBlock = id => collapsedBlocks.indexOf(id) !== -1;

  const setQuestionResultId = outcome => {
    if (currentResult && currentResult.validationResultId !== outcome) {
      setResult(activeQuestion, 'validationResultId', outcome, !!readonly);
    }
  };

  /* if the active question is dependent dropdown.
  * When required, can be generalized for other types of dependent questions
  */
  const parentOfActiveQuestion = activeSection
    ?.questions
    .find(question => {
      return question.datasheetQuestion?.id ===
        activeQuestion?.datasheetQuestion?.currentVersion?.dependentDropdownSettings?.dependsOn;
    })
    || null;

  return (
    <DatasheetStyles.InfoOverlay isSidebarExpanded={isSidebarExpanded} reducedWidth={!isInternalUser}>
      <DatasheetStyles.InfoOverlayContent>
        {activeQuestion && (
          <ResultSectionToggleButtons
            activeReportReviewNote={activeReportReviewNote}
            addNote={addNote}
            isCreateNote={isCreateNote}
            isUnderReviewing={isUnderReviewing}
            selectedQuestion={selectedQuestion}
            setShowHistory={setShowHistory}
            setShowNote={setShowNote}
            setShowResult={setShowResult}
            showNote={showNote}
            showResult={showResult}
          />
        )}
        <ReportReviewBlock
          activeReportReviewComments={activeReportReviewComments}
          activeReportReviewNote={activeReportReviewNote}
          isCreateNote={isCreateNote}
          isUnderReviewing={isUnderReviewing}
          jobId={jobId}
          refetchReportReview={refetchReportReview}
          setIsCreateNote={setIsCreateNote}
          setShowNote={setShowNote}
          setShowResult={setShowResult}
          showNote={showNote}
          type="datasheet"
          typeId={datasheetId}
          typeQuestionId={activeQuestion && activeQuestion.id}
          typeVersionId={datasheetCurrentVersionId}
        />
        {
          !isCreateNote && showHistory && (
            <ResultHistoriesBlock resultHistories={resultHistories} />
          )
        }
        {activeQuestion && !isCreateNote && showResult && (
          <Fragment>
            <ChecklistStyles.QuestionText>
              {activeQuestionCurrentVersion?.text}
            </ChecklistStyles.QuestionText>
            <ChecklistStyles.QuestionDescription>
              {activeQuestionCurrentVersion?.description}
            </ChecklistStyles.QuestionDescription>

            <ChecklistStyles.QuestionResultWrapper error={isBlockSaveFailed('resultId')}>
              <ChecklistStyles.QuestionResultSelectCompliant
                onClick={() => setQuestionResultId(positiveOutcome.resultId)}
                selected={getResult(activeQuestion).validationResultId === positiveOutcome.resultId}
              >
                <span>{positiveOutcome.result.currentVersion.code}</span>
              </ChecklistStyles.QuestionResultSelectCompliant>
              <ChecklistStyles.QuestionResultSelectNonCompliant
                onClick={() => setQuestionResultId(negativeOutcome.resultId)}
                selected={getResult(activeQuestion).validationResultId === negativeOutcome.resultId}
              >
                <span>{negativeOutcome.result.currentVersion.code}</span>
              </ChecklistStyles.QuestionResultSelectNonCompliant>
              {outcomes
                .filter(v => [
                    positiveOutcome.resultId,
                    negativeOutcome.resultId,
                  ].indexOf(v.resultId) === -1,
                )
                .map(outcome => (
                  <ChecklistStyles.QuestionResultSelectNA
                    key={outcome.resultId}
                    onClick={() => setQuestionResultId(outcome.resultId)}
                    selected={getResult(activeQuestion).validationResultId === outcome.resultId}
                  >
                    <span>{outcome.result.currentVersion.code}</span>
                  </ChecklistStyles.QuestionResultSelectNA>
                ))}
            </ChecklistStyles.QuestionResultWrapper>
            {ALERT_CODES.includes(activeQuestion.negativeAlert || activeQuestionCurrentVersion?.negativeResultAlert) ? (
              <ChecklistStyles.AlertNotification>
                <ChecklistStyles.AlertNotificationIcon name="alert" />
                {t('ALERT_24_REQUIRED')}
              </ChecklistStyles.AlertNotification>
            ) : null}
            {getResult(activeQuestion).validationResultId === negativeOutcome.resultId && (
              <QuestionRow
                getResult={getResult}
                getValidationErrors={getValidationErrors}
                isBlockSaveFailed={isBlockSaveFailed}
                isBlockSaveSuccess={isBlockSaveSuccess}
                isRectifiedQuestion
                parentOfActiveQuestion={parentOfActiveQuestion}
                question={activeQuestion}
                readonly={readonly}
                result={getResult(activeQuestion).result}
                sectionOriginalIndex={activeSection.originalIndex}
                selectedQuestion={selectedQuestion}
                setResult={setResult}
                statistic={statistic}
              />
            )}
            {getResult(activeQuestion).resultId === negativeOutcome.resultId ? (
              <Fragment>
                <ChecklistStyles.BlockLabel>
                  {t('FINDINGS')}
                  <ChecklistStyles.ToggleBlock
                    collapsed={isCollapsedBlock(CollapsibleBlock.Legal)}
                    name="expand"
                    onClick={() => toggleBlock(CollapsibleBlock.Legal)}
                  />
                </ChecklistStyles.BlockLabel>
                <FormRow collapsed={isCollapsedBlock(CollapsibleBlock.Legal)}>
                  {(getResult(activeQuestion).nonCompliances || []).map(
                    (nonCompliance, index) => {
                      const nonComplianceLaws = flatten(activeQuestion.checklistQuestion.currentVersion.nonCompliances
                        .filter(v => v.id === nonCompliance.nonComplianceId || v.id === nonCompliance.id)
                        .map(v => v.laws), true);

                      let countryLaws = [];
                      if (factory) {
                        const companyAddress = factory.company.currentVersion.companyAddresses[0];

                        if (companyAddress && companyAddress.currentVersion.country) {
                          countryLaws = companyAddress.currentVersion.country.laws;
                        }
                      }

                      const customLaw = nonCompliance.law ? [{
                        id: nonCompliance.law,
                        currentVersion: {
                          legislationName: nonCompliance.law,
                          articleSecNo: null,
                          legalCitation: null,
                          legalReqSummary: null,
                          internalGuidelineInterpretation: null,
                        },
                        isCustom: true,
                      }] : [];

                      const allLawsOptions = [
                        ...nonComplianceLaws,
                        ...countryLaws,
                        ...customLaw,
                      ].map(v => ({
                        articleSecNo: v.currentVersion.articleSecNo,
                        internalGuidelineInterpretation: v.currentVersion.internalGuidelineInterpretation,
                        label: v.currentVersion.legislationName,
                        legalCitation: v.currentVersion.legalCitation,
                        legalReqSummary: v.currentVersion.legalReqSummary,
                        value: v.id,
                      }));

                      const lawsOptions = (isFiltered ? nonComplianceLaws : countryLaws)
                        .map(v => ({
                          articleSecNo: v.currentVersion.articleSecNo,
                          internalGuidelineInterpretation: v.currentVersion.internalGuidelineInterpretation,
                          label: v.currentVersion.legislationName,
                          legalCitation: v.currentVersion.legalCitation,
                          legalReqSummary: v.currentVersion.legalReqSummary,
                          value: v.id,
                        }));

                      return (
                        <ChecklistStyles.NonComplianceWrapper key={index} error={isBlockSaveFailed(`nonCompliances${index}`)}>
                          <span>{index + 1}.</span>
                          <SelectBox
                            label={t('FORM_NON_COMPLIANCE')}
                            onChange={value => setResultNonCompliance(
                              activeQuestion,
                              index,
                              'nonComplianceId',
                              value ? value.value : null,
                            )}
                            options={nonCompliancesOptions}
                            value={nonCompliancesOptions.find(v => v.value === nonCompliance.nonComplianceId)}
                          />
                          <SelectBox
                            components={{
                              SingleValue: LawSingleValue,
                              Option: LawOption,
                              // eslint-disable-next-line react/display-name
                              MenuList: props => (
                                <LawMenuList
                                  {...props}
                                  isFiltered={isFiltered}
                                  setIsFiltered={setIsFiltered}
                                />
                              ),
                            }}
                            filterOption={({ data }, str) => {
                              if (!str) {
                                return true;
                              }

                              // eslint-disable-next-line no-underscore-dangle
                              if (data.__isNew__) {
                                return true;
                              }

                              const q = str.toLowerCase();

                              return data.articleSecNo.toLowerCase().indexOf(q) !== -1 ||
                                data.label.toLowerCase().indexOf(q) !== -1 ||
                                data.legalCitation.toLowerCase().indexOf(q) !== -1 ||
                                data.legalReqSummary.toLowerCase().indexOf(q) !== -1 ||
                                data.internalGuidelineInterpretation.toLowerCase().indexOf(q) !== -1;
                            }}
                            label={t('FORM_LAW')}
                            onChange={value => setResultNonComplianceByObject(
                              activeQuestion,
                              index,
                              {
                                lawId: value ? value.value : null,
                                law: null,
                              },
                            )}
                            options={lawsOptions}
                            value={allLawsOptions.find(v => v.value === (nonCompliance.lawId || nonCompliance.law))}
                          />
                          <TextField
                            label={t('FORM_LEGAL_REQUIREMENTS')}
                            multiline
                            onChange={e => setResultNonCompliance(
                              activeQuestion,
                              index,
                              'legalRequirements',
                              e.target.value,
                            )}
                            value={nonCompliance.legalRequirements || ''}
                          />
                          <TextField
                            label={t('FORM_DETAILED_FINDING')}
                            multiline
                            onChange={e => setResultNonCompliance(
                              activeQuestion,
                              index,
                              'detailedFinding',
                              e.target.value,
                            )}
                            value={nonCompliance.detailedFinding || ''}
                          />
                          <TextField
                            label={t('FORM_RECOMMENDATIONS')}
                            multiline
                            onChange={e => setResultNonCompliance(
                              activeQuestion,
                              index,
                              'recommendations',
                              e.target.value,
                            )}
                            value={nonCompliance.recommendations || ''}
                          />
                          <ChecklistStyles.RemoveButton
                            name="remove"
                            onClick={() => removeResultNonCompliance(activeQuestion, index)}
                          />
                        </ChecklistStyles.NonComplianceWrapper>
                      );
                    },
                  )}
                  <ChecklistStyles.AddButtonWrapper
                    onClick={() => addResultNonCompliance(activeQuestion)}
                  >
                    <ChecklistStyles.AddButton name="plus" />
                    <ChecklistStyles.AddButtonLabel>{t('FINDING')}</ChecklistStyles.AddButtonLabel>
                  </ChecklistStyles.AddButtonWrapper>
                </FormRow>
                <ChecklistStyles.BlockLabel u-mt-60>
                  {t('DETAILS')}
                  <ChecklistStyles.BlockFollowUp
                    onClick={() => setDisplayFollowUp(!displayFollowUp)}
                  >
                    {displayFollowUp
                      ? t('HIDE_FOLLOW_UP')
                      : t('SHOW_FOLLOW_UP')}
                  </ChecklistStyles.BlockFollowUp>
                </ChecklistStyles.BlockLabel>
                <FormRow collapsed={isCollapsedBlock('detail')}>
                  {displayFollowUp ? (
                    <Fragment>
                      <DatePickerField
                        dateOnly
                        error={isBlockSaveFailed('correctedDate') ? 'Fail to save CORRECTED DATE' : null}
                        label={t('FORM_CORRECTED_DATE')}
                        onChange={value => setResult(activeQuestion, 'correctedDate', value)}
                        value={getResult(activeQuestion).correctedDate || ''}
                      />
                      <TextField
                        error={isBlockSaveFailed('correctiveActionTaken') ? 'Fail to save CORRECTIVE ACTION TAKEN' : null}
                        label={t('FORM_CORRECTIVE_ACTION_TAKEN')}
                        multiline
                        onChange={e => setResult(
                          activeQuestion,
                          'correctiveActionTaken',
                          e.target.value,
                        )}
                        value={getResult(activeQuestion).correctiveActionTaken || ''}
                      />
                    </Fragment>
                  ) : null}
                </FormRow>
              </Fragment>
            ) : null}

            <ChecklistStyles.BlockLabel>
              {t('COMMENTS')}
              <ChecklistStyles.ToggleBlock
                collapsed={isCollapsedBlock(CollapsibleBlock.Comments)}
                name="expand"
                onClick={() => toggleBlock(CollapsibleBlock.Comments)}
              />
            </ChecklistStyles.BlockLabel>
            <FormRow collapsed={isCollapsedBlock(CollapsibleBlock.Comments)}>
              <TextField
                error={isBlockSaveFailed('comments') ? 'Fail to save comments' : null}
                fullsize={'true'}
                multiline
                onBlur={e => setResult(activeQuestion, 'comments', e.target.value)}
                onChange={e => setComments(e.target.value)}
                value={comments || ''}
              />
            </FormRow>

            <Fragment>
              <ChecklistStyles.BlockLabel>
                {t('ATTACHMENTS')}
                <ChecklistStyles.ToggleBlock
                  collapsed={isCollapsedBlock(CollapsibleBlock.Attachments)}
                  name="expand"
                  onClick={() => toggleBlock(CollapsibleBlock.Attachments)}
                />
              </ChecklistStyles.BlockLabel>
              <FormRow collapsed={isCollapsedBlock(CollapsibleBlock.Attachments)}>
                <Attachments
                  key={JSON.stringify(activeQuestion)}
                  attachmentIds={getResultAttachments(activeQuestion)}
                  disabled={readonly}
                  entity="attachments"
                  onChange={(_filename, _entity, id, meta) => {
                    setResult(
                      activeQuestion,
                      'attachments',
                      (getResult(activeQuestion).attachments || []).concat({ attachmentId: id }),
                    );

                    const currentMeta = (
                      getResult(activeQuestion).attachmentsMeta || []
                    ).map(({ __typename, ...rest }) => ({ ...rest }));

                    setResult(
                      activeQuestion,
                      'attachmentsMeta',
                      currentMeta.concat(meta),
                    );
                  }}
                  onDeleteFile = {readonly ? null : onDeletingAttachment}
                  pathPrefix={`job_${jobId}`}
                />
              </FormRow>
            </Fragment>

            {(hasSaqService || hasUnverifiedSaqService) && !showNote && <Fragment>
              <ChecklistStyles.BlockLabel>
                {t('EXTERNAL_ATTACHMENTS')}
                <ChecklistStyles.ToggleBlock
                  collapsed={isExtAttchmentCollapsed}
                  name="expand"
                  onClick={() => {
                    toggleBlock(CollapsibleBlock.ExternalAttachments);
                    setIsExtAttchmentCollapsed(!isExtAttchmentCollapsed);
                  }}
                />
              </ChecklistStyles.BlockLabel>
              <FormRow collapsed={isExtAttchmentCollapsed}>
                <Attachments
                  key={JSON.stringify(activeQuestion)}
                  allowRemove={!!readonly}
                  attachmentIds={getResultExternalAttachments(activeQuestion)}
                  disabled={!!readonly}
                  entity="attachments"
                  labelFileProcessingComplete=""
                  labelTapToUndo=""
                  onChange={(_filename, _entity, id, meta) => {
                    setResult(
                      activeQuestion,
                      'externalAttachments',
                      (getResult(activeQuestion).externalAttachments || []).concat({ attachmentId: id }),
                    );

                    const currentMeta = (
                      getResult(activeQuestion).externalAttachmentsMeta || []
                    ).map(({ __typename, ...rest }) => ({ ...rest }));

                    setResult(
                      activeQuestion,
                      'externalAttachmentsMeta',
                      currentMeta.concat(meta),
                    );
                  }}
                  onDeleteFile={readonly ? null: onDeletingExternalAttachment}
                  pathPrefix={`job_${jobId}`}
                />
              </FormRow>
            </Fragment>}
          </Fragment>
        )}
      </DatasheetStyles.InfoOverlayContent>
    </DatasheetStyles.InfoOverlay>
  );
};

export const ResultFamilySection = React.memo(withJobContext(ResultFamilySectionComponent));

ResultFamilySection.propTypes = ResultFamilySectionPropTypes;
